// TODO update this to import from commons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import {
  Input_core_apimessages_CheckoutUpdateShippingEstimateRequest as UpdateShippingEstimateRequest,
  Input_core_apimessages_CheckoutFinalizeDigitalWalletPaymentRequest as FinalizeDigitalWalletPaymentRequest,
  core_apimessages_CheckoutAction_Type as CheckoutActionType,
  core_apimessages_CheckoutFinalizeDigitalWalletPaymentRequest_ResultConfirmation as ResultConfirmation,
} from '@reverbdotcom/commons/src/gql/graphql';
import {
  CheckoutResult,
  mapToAuthError,
  mapPartialAddressToEntry,
  mapPaymentDataAddressToEntry,
  Checkout,
  mapDisplayItems,
  mapToShippingError,
  mapToAuthValidationError,
  mapOptionalGuestEntry,
} from './googlePayHelpers';

export type CheckoutStartHandler = () => Promise<CheckoutResult>;

export type UpdateShippingEstimateHandler = (input: UpdateShippingEstimateRequest) => Promise<CheckoutResult>;

export type FinalizeCheckoutHandler = (input: FinalizeDigitalWalletPaymentRequest) => Promise<CheckoutResult>;

export type CheckoutSuccessHandler = (redirectUrl: string) => any;

interface GooglePayManagerConfig {
  startCheckout: CheckoutStartHandler;
  updateShippingEstimate: UpdateShippingEstimateHandler;
  finalizeCheckout: FinalizeCheckoutHandler;
  countryCode: string;
  resultConfirmation: ResultConfirmation;
  onSuccess: CheckoutSuccessHandler;
}

export class GooglePayManager {
  private startCheckout: CheckoutStartHandler;

  private updateShippingEstimate: UpdateShippingEstimateHandler;

  private finalizeCheckout: FinalizeCheckoutHandler;

  private startCheckoutPromise: Promise<CheckoutResult>;

  private onSuccess: CheckoutSuccessHandler;

  private _checkout: Checkout = null;

  private countryCode: string;

  private resultConfirmation: ResultConfirmation;

  constructor(config: GooglePayManagerConfig) {
    this.startCheckout = config.startCheckout;
    this.updateShippingEstimate = config.updateShippingEstimate;
    this.finalizeCheckout = config.finalizeCheckout;
    this.countryCode = config.countryCode;
    this.resultConfirmation = config.resultConfirmation;
    this.onSuccess = config.onSuccess;
  }

  changePaymentData = async (
    intermediatePaymentData: google.payments.api.IntermediatePaymentData,
  ): Promise<google.payments.api.PaymentDataRequestUpdate> => {
    const result = await this.fetchCheckout();

    if (result.type === 'failure') {
      return {
        error: mapToShippingError(result.errors),
      };
    }

    return this.updateShipping(result.checkout, intermediatePaymentData);
  };

  loadPaymentData = async () => {
    const result = await this.fetchCheckout();

    if (result.type === 'failure') {
      return;
    }

    const { action } = result.checkout;

    if (REDIRECT_ACTION_TYPES.includes(action?.type) && action.redirectUrl) {
      this.onSuccess(action.redirectUrl);
    }
  };

  authorizePayment = async (
    paymentData: google.payments.api.PaymentData,
  ): Promise<google.payments.api.PaymentAuthorizationResult> => {
    const result = await this.fetchCheckout();

    if (result.type === 'failure') {
      return {
        transactionState: 'ERROR',
      };
    }

    return this.finalize(result.checkout, paymentData);
  };

  private updateShipping = async (
    previousCheckout: Checkout,
    intermediatePaymentData: google.payments.api.IntermediatePaymentData,
  ): Promise<google.payments.api.PaymentDataRequestUpdate> => {
    const result = await this.updateShippingEstimate({
      id: previousCheckout.id,
      shippingAddress: mapPartialAddressToEntry(intermediatePaymentData.shippingAddress),
    });

    if (result.type === 'failure') {
      return { error: mapToShippingError(result.errors) };
    }

    const checkout = this.updateCheckout(result.checkout);

    return {
      newTransactionInfo: this.mapCheckoutToTransactionInfo(checkout),
    };
  };

  private finalize = async (
    previousCheckout: Checkout,
    paymentData: google.payments.api.PaymentData,
  ): Promise<google.payments.api.PaymentAuthorizationResult> => {
    const result = await this.finalizeCheckout({
      id: previousCheckout.id,
      paymentToken: paymentData.paymentMethodData.tokenizationData.token,
      totalPrice: {
        amountCents: previousCheckout.total.amount.amountCents,
        currency: previousCheckout.total.amount.currency,
      },
      guestUser: mapOptionalGuestEntry({ paymentData, checkout: previousCheckout }),
      shippingAddress: mapPaymentDataAddressToEntry({ paymentData, checkout: previousCheckout }),
      resultConfirmation: this.resultConfirmation,
    });

    if (result.type === 'failure') {
      return {
        transactionState: 'ERROR',
        error: mapToAuthValidationError(result.errors),
      };
    }

    const checkout = this.updateCheckout(result.checkout);

    if (REDIRECT_ACTION_TYPES.includes(checkout.action.type)) {
      return {
        transactionState: 'SUCCESS',
      };
    }

    return {
      transactionState: 'ERROR',
      error: mapToAuthError(checkout),
    };
  };

  private updateCheckout = (checkout: Checkout) => {
    this._checkout = checkout;

    return this._checkout;
  };

  /**
   * Resolves one and only one initial checkout. Handles the case where the
   * Google Pay button may call the "change payment data" before the
   * initial checkout has been resolved.
   */
  private fetchCheckout = async (): Promise<CheckoutResult> => {
    if (this._checkout) {
      return { type: 'success', checkout: this._checkout };
    }

    if (!this.startCheckoutPromise) {
      this.startCheckoutPromise = this.startCheckout();
    }

    const result = await this.startCheckoutPromise;

    if (result.type !== 'failure') {
      this.updateCheckout(result.checkout);
    }

    return result;
  };

  private mapCheckoutToTransactionInfo = (checkout: Checkout): google.payments.api.TransactionInfo => {
    return {
      transactionId: checkout.id,
      totalPriceStatus: 'FINAL',
      totalPrice: checkout.total.amount.amount,
      totalPriceLabel: checkout.total.label,
      currencyCode: checkout.total.amount.currency,
      countryCode: this.countryCode,
      displayItems: mapDisplayItems(checkout),
    };
  };
}

const REDIRECT_ACTION_TYPES = [
  CheckoutActionType.PROCESSING_PAYMENT,
  CheckoutActionType.CHECKOUT_SUCCESS,
];

export const GooglePayManagerFragment = gql`
  fragment GooglePayManagerFields on Checkout {
    _id
    id
    status
    paymentErrorMessage
    type
    total {
      label
      amount {
        amount
        amountCents
        currency
      }
    }
    guest {
      email
      firstName
      lastName
    }
    shippingAddress {
      _id
      name
      streetAddress
      extendedAddress
      locality
      region
      postalCode
      countryCode
      phone
    }
    lineItems {
      type
      label
      amount {
        amount
      }
    }
  }
`;

export const GooglePayFinalizeActionFragment = gql`
  fragment GooglePayFinalizeActionFields on CheckoutAction {
    _id
    type
    redirectUrl
  }
`;
